'use client'

import { useEffect, useState } from 'react'
import { Dialog, Divider, Navigation, Spacer } from '@vinted/web-ui'

import {
  AddressModel,
  businessAccountFromUserDto,
  setBusinessAccountAddresses,
  viewScreenEvent,
} from '@marketplace-web/business-accounts/business-accounts-data'
import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { AddressField } from '@marketplace-web/address/address-data'

import AddressForm from '../../../AddressForm'

type BusinessAddressModalProps = {
  businessAddress: AddressModel | null
  returnAddress: AddressModel | null
  isBusinessAddressModalOpen: boolean
  onToggleBusinessAddressModal: () => void
}

const BusinessAddressModal = ({
  businessAddress,
  returnAddress,
  isBusinessAddressModalOpen,
  onToggleBusinessAddressModal,
}: BusinessAddressModalProps) => {
  const { track } = useTracking()
  const translate = useTranslate('profile_settings.business_addresses')
  const businessAccountId = businessAccountFromUserDto(useSession().user)?.id as number
  const [errors, setErrors] = useState<Array<ErrorItem>>([])

  useEffect(() => {
    if (!isBusinessAddressModalOpen) return

    track(viewScreenEvent({ screen: 'edit_business_address' }))
  }, [track, isBusinessAddressModalOpen])

  function onCancel() {
    onToggleBusinessAddressModal()
  }

  async function onSubmit(address: AddressModel, isFormValid: boolean) {
    if (!isFormValid) return
    const response = await setBusinessAccountAddresses({
      businessAccountId,
      returnAddress,
      businessAddress: address,
    })

    if ('errors' in response) {
      setErrors(response.errors)

      return
    }

    onCancel()
  }

  function renderNavigation() {
    return (
      <Navigation
        theme="transparent"
        body={
          <>
            <Spacer size={Spacer.Size.Medium} />
            {translate('business_address.title')}
            <Spacer size={Spacer.Size.Medium} />
          </>
        }
      />
    )
  }

  const translations = {
    name: {
      title: translate('business_address.form_name.title'),
      placeholder: translate('business_address.form_name.placeholder'),
    },
    buttons: {
      continue: translate('buttons.submit'),
    },
  }

  const fieldConfiguration = [
    {
      field: AddressField.Country,
      disabled: true,
    },
    {
      field: AddressField.Name,
      disabled: true,
    },
    {
      field: AddressField.Line1,
      required: true,
    },
    {
      field: AddressField.Line2,
    },
    {
      field: AddressField.PostalCode,
      required: true,
    },
    {
      field: AddressField.City,
      required: true,
    },
  ]

  const renderAddressForm = () => (
    <AddressForm
      isSubmitPending={false}
      defaultValues={businessAddress}
      onSubmit={onSubmit}
      onCancel={onCancel}
      errors={errors}
      translations={translations}
      fieldConfiguration={fieldConfiguration}
    />
  )

  return (
    <Dialog show={isBusinessAddressModalOpen} testId="address-modal">
      {renderNavigation()}
      <Divider />
      {renderAddressForm()}
    </Dialog>
  )
}

export default BusinessAddressModal
